/**
 * SCH-5.9 — sync-health metrics service.
 *
 * Wraps two endpoints:
 *   - GET /sync-metrics        (settings-manager; the caller's own tenant)
 *   - GET /admin/sync-metrics  (superadmin; per-tenant rollup, AC4)
 *
 * Mirrors backfillProgressService conventions: shared envelope fetch, snake↔camel translation at
 * the boundary, HTTP 403 surfaced as a typed error rather than a thrown exception.
 *
 * ⚠️ The mapping below uses `?? null`, never `?? 0`, for successRate and avgDurationMs. A bucket
 * with no runs has no success rate; coercing that to 0 draws the chart's line to the floor and
 * reads as a total outage on a day when nothing was scheduled. Postgres, PostgREST and the Python
 * response model all preserve the null — this is the last place it can be thrown away, and it is
 * the easiest place to do so by accident.
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  CrossTenantSyncMetricsResponse,
  SyncMetricsBucketSize,
  SyncMetricsPoint,
  SyncMetricsRange,
  SyncMetricsResponse,
  SyncMetricsResult,
  TenantSyncHealth,
} from '@/types/integrations/syncMetrics';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

interface RawPoint {
  bucket_start: string;
  total_runs: number;
  successful_runs: number;
  success_rate: number | null;
  avg_duration_ms: number | null;
  projects_changed: number;
  auth_failures: number;
}

interface RawMetricsResponse {
  range: SyncMetricsRange;
  bucket: SyncMetricsBucketSize;
  data: RawPoint[];
}

interface RawTenantHealth {
  tenant_id: string;
  total_runs: number;
  successful_runs: number;
  success_rate: number | null;
  sparkline: RawPoint[];
}

interface RawCrossTenantResponse {
  range: SyncMetricsRange;
  data: RawTenantHealth[];
}

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string } | null;
  meta: unknown;
}

async function fetchEnvelope<TData>(
  path: string,
): Promise<ApiEnvelope<TData> | { _http_403: true }> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, { method: 'GET', headers });

  // Admin-gated endpoints raise HTTP 403 from the FastAPI dependency, not as an envelope error.
  if (response.status === 403) {
    return { _http_403: true };
  }
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

function _mapPoint(raw: RawPoint): SyncMetricsPoint {
  return {
    bucketStart: raw.bucket_start,
    totalRuns: raw.total_runs ?? 0,
    successfulRuns: raw.successful_runs ?? 0,
    // `?? null`, NOT `?? 0` — see the module comment.
    successRate: raw.success_rate ?? null,
    avgDurationMs: raw.avg_duration_ms ?? null,
    projectsChanged: raw.projects_changed ?? 0,
    authFailures: raw.auth_failures ?? 0,
  };
}

async function getSyncMetrics(
  range: SyncMetricsRange,
  bucket: SyncMetricsBucketSize,
): Promise<SyncMetricsResult<SyncMetricsResponse>> {
  const params = new URLSearchParams({ range, bucket });
  const path = `/api/v1/integrations/subcontractorhub/sync-metrics?${params.toString()}`;

  const result = await fetchEnvelope<RawMetricsResponse>(path);
  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message: 'Administrator access is required to view sync insights.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: result.error };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return {
    data: {
      range: result.data.range,
      bucket: result.data.bucket,
      data: (result.data.data ?? []).map(_mapPoint),
    },
    error: null,
  };
}

async function getCrossTenantSyncMetrics(
  range: SyncMetricsRange,
): Promise<SyncMetricsResult<CrossTenantSyncMetricsResponse>> {
  const params = new URLSearchParams({ range });
  const path = `/api/v1/integrations/subcontractorhub/admin/sync-metrics?${params.toString()}`;

  const result = await fetchEnvelope<RawCrossTenantResponse>(path);
  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message: 'Superadmin access is required to view the cross-tenant rollup.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: result.error };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  const tenants: TenantSyncHealth[] = (result.data.data ?? []).map((raw) => ({
    tenantId: raw.tenant_id,
    totalRuns: raw.total_runs ?? 0,
    successfulRuns: raw.successful_runs ?? 0,
    successRate: raw.success_rate ?? null,
    sparkline: (raw.sparkline ?? []).map(_mapPoint),
  }));
  return { data: { range: result.data.range, data: tenants }, error: null };
}

export const syncMetricsService = {
  getSyncMetrics,
  getCrossTenantSyncMetrics,
};
